import React, { useEffect, useState, useCallback } from 'react';
import { getSermons } from '../services/api';
import './Sermons.css';

const PAGE_SIZE = 9;

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

/** Extract a YouTube video ID from various URL formats or bare IDs. */
function getYouTubeId(url) {
  if (!url) return null;
  // Already a bare ID (11 chars, no slash/dot)
  if (/^[A-Za-z0-9_-]{11}$/.test(url.trim())) return url.trim();
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1);
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const embedMatch = u.pathname.match(/\/embed\/([A-Za-z0-9_-]{11})/);
    if (embedMatch) return embedMatch[1];
  } catch (_) { /* not a valid URL */ }
  return null;
}

function getThumbnail(videoUrl) {
  const id = getYouTubeId(videoUrl);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export default function Sermons() {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getSermons()
      .then(setSermons)
      .catch(() => setError('Impossible de charger les sermons.'))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setSelected(null), []);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, close]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filter]);

  const series = [...new Set(sermons.map((s) => s.series).filter(Boolean))];

  const filtered = sermons.filter((s) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      s.title.toLowerCase().includes(q) ||
      s.speaker.toLowerCase().includes(q) ||
      (s.scripture_reference || '').toLowerCase().includes(q);
    const matchSeries = !filter || s.series === filter;
    return matchSearch && matchSeries;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const slice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="sermons-page">
      <div className="page-banner">
        <div className="container">
          <h1>Sermons</h1>
          <p>Nourrissez votre âme avec la Parole de Dieu</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="sermons-filters">
            <input
              type="search"
              className="filter-input"
              placeholder="Rechercher un sermon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="">Toutes les séries</option>
              {series.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {loading && <div className="spinner-wrap"><div className="spinner" /></div>}
          {error && <div className="state-box"><h3>Erreur</h3><p>{error}</p></div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="state-box">
              <h3>Aucun sermon trouvé</h3>
              <p>Essayez une autre recherche ou revenez bientôt.</p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="sermons-grid">
                {slice.map((s) => (
                  <SermonCard key={s.id} sermon={s} onClick={() => setSelected(s)} />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination current={page} total={totalPages} onChange={setPage} />
              )}
            </>
          )}
        </div>
      </section>

      {selected && <SermonModal sermon={selected} onClose={close} />}
    </div>
  );
}

function SermonCard({ sermon, onClick }) {
  const thumb = getThumbnail(sermon.video_url);
  const hasVideo = !!getYouTubeId(sermon.video_url);

  return (
    <article
      className={`sermon-card card${hasVideo ? ' sermon-card--playable' : ''}`}
      onClick={hasVideo ? onClick : undefined}
      role={hasVideo ? 'button' : undefined}
      tabIndex={hasVideo ? 0 : undefined}
      onKeyDown={hasVideo ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="sermon-card__img-wrap">
        {thumb ? (
          <img src={thumb} alt={sermon.title} className="sermon-card__img" />
        ) : (
          <div className="sermon-card__img-placeholder">🏛️</div>
        )}
        <div className="sermon-card__img-overlay" />
        {sermon.series && (
          <span className="badge badge-gold sermon-card__series-badge">{sermon.series}</span>
        )}
        {hasVideo && (
          <div className="sermon-card__play-btn" aria-hidden="true">▶</div>
        )}
      </div>
      <div className="sermon-card__body">
        <h3 className="sermon-card__title">{sermon.title}</h3>
        <p className="sermon-card__speaker">par {sermon.speaker}</p>
        <p className="sermon-card__date">{formatDate(sermon.date)}</p>
        {sermon.scripture_reference && (
          <p className="sermon-card__scripture">📖 {sermon.scripture_reference}</p>
        )}
        {sermon.description && (
          <p className="sermon-card__desc">{sermon.description}</p>
        )}
        {hasVideo && (
          <span className="sermon-card__cta">Regarder le sermon ›</span>
        )}
      </div>
    </article>
  );
}

function SermonModal({ sermon, onClose }) {
  const videoId = getYouTubeId(sermon.video_url);

  return (
    <div className="sm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sm-close" onClick={onClose} aria-label="Fermer">&times;</button>

        {/* YouTube embed */}
        <div className="sm-video-wrap">
          <iframe
            className="sm-video"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={sermon.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="sm-body">
          {sermon.series && (
            <span className="badge badge-gold" style={{ marginBottom: 8, display: 'inline-block' }}>{sermon.series}</span>
          )}
          <h2 className="sm-title">{sermon.title}</h2>
          <div className="sm-meta">
            <span>👤 {sermon.speaker}</span>
            <span>📅 {formatDate(sermon.date)}</span>
            {sermon.scripture_reference && <span>📖 {sermon.scripture_reference}</span>}
          </div>
          {sermon.description && (
            <p className="sm-desc">{sermon.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Pagination({ current, total, onChange }) {
  const go = (p) => { onChange(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  return (
    <div className="pagination">
      <button className="pagination__btn" disabled={current === 1} onClick={() => go(current - 1)} aria-label="Page précédente">‹</button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`el-${i}`} className="pagination__ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`pagination__btn${p === current ? ' pagination__btn--active' : ''}`}
            onClick={() => go(p)}
            aria-current={p === current ? 'page' : undefined}
          >{p}</button>
        )
      )}
      <button className="pagination__btn" disabled={current === total} onClick={() => go(current + 1)} aria-label="Page suivante">›</button>
    </div>
  );
}

